import { Injectable, inject, signal, computed } from '@angular/core';
import { Firestore, doc, getFirestore, onSnapshot, setDoc, Unsubscribe } from 'firebase/firestore';

import { Organization } from '../models/organization.model';
import { OrganizationMember } from '../models/member.model';
import { Role, DEFAULT_MEMBER_PERMISSIONS } from '../models/role.model';
import { HOME_REGION, type RegionConnection } from '@crewdle/auth-bridge';
import { FirebaseError } from 'firebase/app';

import { AuthBridgeService } from './auth-bridge.service';
import { DataStoreService } from './data-store.service';
import { FirebaseService } from './firebase.service';
import { OrganizationService } from './organization.service';
import { OrganizationMemberService } from './organization-member.service';
import { RoleService } from './role.service';

@Injectable({
  providedIn: 'root',
})
export class OrgContextService {
  private readonly firebase = inject(FirebaseService);
  private readonly dataStore = inject(DataStoreService);
  private readonly organizationService = inject(OrganizationService);
  private readonly memberService = inject(OrganizationMemberService);
  private readonly roleService = inject(RoleService);
  private readonly authBridge = inject(AuthBridgeService);

  readonly currentOrg = computed(() => this.dataStore.currentOrganization());
  readonly currentMember = computed(() => this.dataStore.currentMember());
  readonly currentRole = computed(() => this.dataStore.currentRole());

  readonly userOrganizations = signal<Organization[]>([]);
  readonly loading = signal(false);
  /**
   * Orgs the user belongs to whose region can't be reached right now (no
   * config in this build, region not live, region-token refused) → reason.
   */
  readonly unavailableOrgs = signal<Record<string, string>>({});

  private currentOrgUnsub: Unsubscribe | null = null;
  private currentMemberUnsub: Unsubscribe | null = null;
  private currentRoleUnsub: Unsubscribe | null = null;
  private subscribedOrgId: string | null = null;
  private subscribedRoleId: string | null = null;

  // The org the user last selected, shared across all Crewdle apps via the user
  // doc (`users/{uid}.currentOrganizationId`). Captured in loadUserOrganizations.
  private remoteCurrentOrgId: string | null = null;
  // Memoized connectRegion per `${uid}:${orgId}` (see connectOrgRegion).
  private readonly regionConnections = new Map<string, Promise<RegionConnection>>();

  private get storageKey(): string {
    const uid = this.firebase.auth.currentUser?.uid;
    return `admin-console-current-org-${uid}`;
  }

  async init() {
    await this.firebase.auth.authStateReady();
    const user = this.firebase.auth.currentUser;
    if (!user) return;

    await this.loadUserOrganizations();

    // If the user just completed a Microsoft sign-in, the centralized auth
    // portal stashed the Entra tid + oid in a short-lived `.crewdle.ai` cookie
    // so we can run org auto-discovery (every Crewdle org linked to that tenant
    // the user is assigned to gets joined).
    const pendingTid = this.readMsCookie('ms_tid');
    const pendingOid = this.readMsCookie('ms_oid');
    if (pendingTid) {
      this.clearMsCookie('ms_tid');
      this.clearMsCookie('ms_oid');
      try {
        await this.organizationService.syncMicrosoftRoleOnLogin(pendingTid, pendingOid ?? undefined);
        await this.loadUserOrganizations();
      } catch {
        // Non-blocking — fall through to whatever orgs we already have.
      }
    } else {
      // Routine role refresh for orgs the user already belongs to.
      this.organizationService.syncMicrosoftRoleOnLogin().catch(() => {});
    }

    // Restore the active org. The user-doc `currentOrganizationId` is the
    // cross-app source of truth (shared with connect/open-webui); fall back to
    // this app's localStorage, then the first org.
    const orgs = this.userOrganizations();
    const savedOrgId = localStorage.getItem(this.storageKey);
    const target = [this.remoteCurrentOrgId, savedOrgId].find(
      (id): id is string => !!id && orgs.some((o) => o.id === id),
    );

    // Data residency: the shared current org lives in a region this build/stack
    // can't reach → surface "region unavailable" rather than silently landing
    // the user in another (ca) org.
    const unavailable = this.unavailableOrgs();
    const pinned = [this.remoteCurrentOrgId, savedOrgId].find((id): id is string => !!id && id in unavailable);
    if (!target && pinned) {
      this.firebase.bindUnavailable(pinned, unavailable[pinned]);
      return;
    }

    if (target) {
      await this.switchOrg(target);
    } else if (orgs.length > 0) {
      await this.switchOrg(orgs[0].id);
    }
  }

  /** Read a `crewdle_<name>` cookie set by the auth portal on `.crewdle.ai`. */
  private readMsCookie(name: string): string | null {
    const prefix = `crewdle_${name}=`;
    for (const c of document.cookie.split(';')) {
      const t = c.trim();
      if (t.startsWith(prefix)) return decodeURIComponent(t.substring(prefix.length));
    }
    return null;
  }

  /** Expire a `crewdle_<name>` cookie once consumed (must match set domain). */
  private clearMsCookie(name: string): void {
    document.cookie = `crewdle_${name}=;max-age=0;path=/;domain=.crewdle.ai`;
  }

  async loadUserOrganizations() {
    const { getDoc, doc } = await import('firebase/firestore');
    const user = this.firebase.auth.currentUser;
    if (!user) return;

    // users/{uid} is GLOBAL (ca project) — never the regional mirror.
    const userDoc = await getDoc(doc(this.firebase.globalFirestore, 'users', user.uid));
    const userData = userDoc.data();
    const orgIds: string[] = userData?.['organizationIds'] || [];
    // Cross-app shared selection (same field open-webui/connect use).
    this.remoteCurrentOrgId = (userData?.['currentOrganizationId'] as string) || null;

    // Load every org in parallel rather than serially — this is on the cold
    // bootstrap path, so N serial round trips here directly delays first paint.
    // Pass the orgIds we just read so each getOrganization skips its own
    // redundant user-doc access re-check. Orgs the user can no longer access
    // resolve to null and are dropped.
    //
    // Data residency: each org doc is read from ITS region's project
    // (connectRegion is shared per org and signs a region in once per uid).
    const unavailable: Record<string, string> = {};
    const orgs = (
      await Promise.all(
        orgIds.map(async (orgId) => {
          let db: Firestore;
          try {
            db = await this.firestoreForOrg(orgId);
          } catch (err) {
            unavailable[orgId] = err instanceof Error ? err.message : String(err);
            return null;
          }
          return this.organizationService.getOrganization(orgId, false, orgIds, db).catch(() => null);
        }),
      )
    ).filter((o): o is Organization => o !== null);

    this.unavailableOrgs.set(unavailable);
    this.userOrganizations.set(orgs);
  }

  async refreshCurrentOrg() {
    const orgId = this.currentOrg()?.id;
    if (!orgId) return;
    await this.organizationService.getOrganization(orgId, true);
  }

  /**
   * User-initiated org switch: persists the choice and hard-reloads so every
   * service / signal / Firestore subscription rehydrates against the new org.
   * Mirrors dev-console and the main chat app.
   */
  switchOrgAndReload(orgId: string): void {
    const userOrgs = this.userOrganizations();
    if (!userOrgs.some((o) => o.id === orgId)) {
      throw new Error('Unauthorized: user is not a member of this organization');
    }
    localStorage.setItem(this.storageKey, orgId);
    // Persist the shared selection BEFORE reloading — init() prefers the
    // user-doc currentOrganizationId, so without this the reload would restore
    // the previous (stale) shared org instead of the one just selected.
    const uid = this.firebase.auth.currentUser?.uid;
    const persisted = uid
      ? setDoc(
          doc(this.firebase.globalFirestore, 'users', uid),
          { currentOrganizationId: orgId },
          { merge: true },
        ).catch(() => undefined)
      : Promise.resolve();
    persisted.finally(() => {
      window.location.href = '/';
    });
  }

  async switchOrg(orgId: string) {
    this.loading.set(true);
    try {
      // Verify user has access to this organization. switchOrg is always
      // called with an org already present in userOrganizations (init() and the
      // invite flows both load it first), so reuse that loaded doc instead of a
      // forced re-fetch — startCurrentOrgSync below opens a live subscription
      // that reconciles any drift within milliseconds.
      const userOrgs = this.userOrganizations();
      const organization = userOrgs.find((o) => o.id === orgId);
      if (!organization) {
        console.error('[OrgContext] switchOrg: user does not have access to organization', orgId);
        throw new Error('Unauthorized: user is not a member of this organization');
      }

      // Ensure system roles (admin, member) exist in Firestore for this org.
      // Fire-and-forget: role resolution below falls back to correct
      // permissions if the docs aren't seeded yet, so this must not block paint.
      // Data residency: bind the org's regional project BEFORE any org read or
      // listener, so every FirebaseService org handle targets the right region.
      await this.bindOrgRegion(orgId);

      this.roleService.seedSystemRolesIfMissing(orgId).catch(() => {});

      let member: OrganizationMember | null = null;
      let role: Role | null = null;

      try {
        member = await this.memberService.getCurrentMember(orgId);
      } catch {
        // Member doc may not exist yet
      }

      if (member) {
        try {
          role = await this.memberService.getCurrentMemberRole(orgId);
        } catch {
          role = this.buildFallbackRoleFromMember(member, organization);
        }
      }

      // Fallback: if still no role but user is org owner, grant admin
      if (!role && organization.ownerId === this.firebase.auth.currentUser?.uid) {
        role = this.buildAdminRole();
      }

      this.dataStore.updateState({
        currentOrganization: organization,
        currentMember: member,
        currentRole: role,
      });

      localStorage.setItem(this.storageKey, orgId);
      // Persist the selection on the user doc so every Crewdle app picks up the
      // same active org (connect/open-webui read this field too). Only write on
      // an actual change — init() calls switchOrg on every load.
      const uid = this.firebase.auth.currentUser?.uid;
      if (uid && this.remoteCurrentOrgId !== orgId) {
        setDoc(
          doc(this.firebase.globalFirestore, 'users', uid),
          { currentOrganizationId: orgId },
          { merge: true },
        ).catch(() => undefined);
      }
      this.remoteCurrentOrgId = orgId;
      this.startCurrentOrgSync(orgId);
    } finally {
      this.loading.set(false);
    }
  }

  /** Firestore of the org's regional project (the global one for ca orgs). */
  private async firestoreForOrg(orgId: string): Promise<Firestore> {
    const connection = await this.connectOrgRegion(orgId);
    return getFirestore(connection.app);
  }

  /**
   * `connectRegion` for an org, memoized per session (uid + org): the org list
   * load and the following switchOrg / repeated init() share ONE orgDirectory
   * read (and, for remote regions, one token exchange) instead of re-reading
   * the directory every time. Failures are not cached, so a retry re-reads.
   */
  private connectOrgRegion(orgId: string): Promise<RegionConnection> {
    const key = `${this.firebase.auth.currentUser?.uid ?? ''}:${orgId}`;
    const cached = this.regionConnections.get(key);
    if (cached) return cached;
    const pending: Promise<RegionConnection> = this.authBridge.bridge
      .connectRegion({
        orgId,
        regionalConfigs: this.firebase.regionalConfigs,
        firestore: this.firebase.globalFirestore,
      })
      .catch((err: unknown) => {
        // Never cache a failure (or the ca fallback below): the next call re-reads.
        if (this.regionConnections.get(key) === pending) this.regionConnections.delete(key);
        if (this.isDirectoryReadFailure(err) && !this.hasRemoteRegion()) {
          // Only ca exists in this environment (prod today), so a failed
          // orgDirectory read (rules not deployed yet, offline, ...) can only
          // mean a ca org — bind the global app rather than "region unavailable".
          // With a remote region configured this stays fail-closed.
          console.warn(`[OrgContext] orgDirectory read failed (${(err as FirebaseError).code}); no remote region configured — using ca`);
          return { region: HOME_REGION, app: this.firebase.globalApp, auth: this.firebase.auth, isRegional: false };
        }
        throw err;
      });
    this.regionConnections.set(key, pending);
    return pending;
  }

  /** A Firestore error from the directory read (auth-bridge's own Errors carry no code). */
  private isDirectoryReadFailure(err: unknown): err is FirebaseError {
    return err instanceof FirebaseError && typeof err.code === 'string';
  }

  private hasRemoteRegion(): boolean {
    return Object.keys(this.firebase.regionalConfigs).length > 0;
  }

  /**
   * Connect the org's region and point FirebaseService's org handles at it.
   * Failure = "region unavailable" (FirebaseService.regionError) — never a
   * fallback to the ca project.
   */
  private async bindOrgRegion(orgId: string): Promise<void> {
    try {
      const connection = await this.connectOrgRegion(orgId);
      this.firebase.bindOrg(orgId, connection.region, connection.app);
    } catch (err) {
      this.stopCurrentOrgSync();
      this.firebase.bindUnavailable(orgId, err instanceof Error ? err.message : String(err));
      throw err;
    }
  }

  /**
   * Keep the current organization doc in sync so remote mutations (e.g.
   * agent-driven rename or Microsoft sync toggle) reflect live in the UI.
   */
  private startCurrentOrgSync(orgId: string): void {
    if (this.subscribedOrgId === orgId) return;
    this.currentOrgUnsub?.();
    this.currentMemberUnsub?.();
    this.currentRoleUnsub?.();
    this.currentRoleUnsub = null;
    this.subscribedRoleId = null;
    this.subscribedOrgId = orgId;

    const orgRef = doc(this.firebase.firestore, 'organizations', orgId);
    this.currentOrgUnsub = onSnapshot(orgRef, (snap) => {
      if (!snap.exists()) return;
      const current = this.dataStore.getProperty('currentOrganization');
      if (!current || current.id !== orgId) return;
      const data = snap.data();
      const updated: Organization = {
        ...current,
        name: data['name'] ?? current.name,
        tids: data['tids'] ?? current.tids,
        creditBalance: data['creditBalance'] ?? current.creditBalance,
        milestones: data['milestones'] ?? current.milestones,
        onboardingProgram: data['onboardingProgram'] ?? current.onboardingProgram,
        // Live partner-status flips (e.g. a become-partner approval) light up
        // white-label branding without a reload.
        isPartner: data['isPartner'] ?? current.isPartner,
        partnerId: data['partnerId'] ?? current.partnerId,
      };
      this.dataStore.updateState({ currentOrganization: updated });
    });

    const uid = this.firebase.auth.currentUser?.uid;
    if (!uid) return;
    const memberRef = doc(this.firebase.firestore, 'organizations', orgId, 'members', uid);
    this.currentMemberUnsub = onSnapshot(memberRef, (snap) => {
      if (this.subscribedOrgId !== orgId) return;
      if (!snap.exists()) {
        this.dataStore.updateState({ currentMember: null });
        this.resubscribeRole(orgId, null);
        return;
      }
      const data = snap.data();
      const updatedMember: OrganizationMember = {
        userId: data['userId'],
        role: data['role'],
        roleId: data['roleId'],
        permissions: data['permissions'],
        joinedAt: data['joinedAt'],
      };
      this.dataStore.updateState({ currentMember: updatedMember });
      const roleId = updatedMember.roleId || updatedMember.role || null;
      this.resubscribeRole(orgId, roleId);
    });
  }

  private resubscribeRole(orgId: string, roleId: string | null): void {
    if (this.subscribedRoleId === roleId) return;
    this.currentRoleUnsub?.();
    this.currentRoleUnsub = null;
    this.subscribedRoleId = roleId;

    if (!roleId) {
      const member = this.dataStore.getProperty('currentMember');
      const organization = this.dataStore.getProperty('currentOrganization');
      const fallback = member && organization
        ? this.buildFallbackRoleFromMember(member, organization)
        : null;
      this.dataStore.updateState({ currentRole: fallback });
      return;
    }

    if (roleId === 'admin') {
      this.dataStore.updateState({ currentRole: this.buildAdminRole() });
      return;
    }

    const roleRef = doc(this.firebase.firestore, 'organizations', orgId, 'roles', roleId);
    this.currentRoleUnsub = onSnapshot(roleRef, (snap) => {
      if (this.subscribedOrgId !== orgId || this.subscribedRoleId !== roleId) return;
      if (!snap.exists()) {
        const member = this.dataStore.getProperty('currentMember');
        const organization = this.dataStore.getProperty('currentOrganization');
        const fallback = member && organization
          ? this.buildFallbackRoleFromMember(member, organization)
          : null;
        this.dataStore.updateState({ currentRole: fallback });
        return;
      }
      const data = snap.data();
      const role: Role = {
        id: snap.id,
        name: data['name'],
        description: data['description'] || '',
        permissions: data['permissions'] || [],
        isSystem: data['isSystem'] ?? true,
        createdAt: data['createdAt'],
        updatedAt: data['updatedAt'],
        createdBy: data['createdBy'] || '',
      };
      this.dataStore.updateState({ currentRole: role });
    });
  }

  stopCurrentOrgSync(): void {
    this.currentOrgUnsub?.();
    this.currentMemberUnsub?.();
    this.currentRoleUnsub?.();
    this.currentOrgUnsub = null;
    this.currentMemberUnsub = null;
    this.currentRoleUnsub = null;
    this.subscribedOrgId = null;
    this.subscribedRoleId = null;
  }

  hasPermission(permission: string) {
    return computed(() => {
      const role = this.currentRole();
      if (!role) {
        return this.isCurrentUserOwner();
      }
      return role.permissions.includes(permission) || role.permissions.includes('*');
    });
  }

  async checkPermission(permission: string): Promise<boolean> {
    const role = this.currentRole();
    if (!role) {
      return this.isCurrentUserOwner();
    }
    return role.permissions.includes(permission) || role.permissions.includes('*');
  }

  private isCurrentUserOwner(): boolean {
    const org = this.currentOrg();
    const uid = this.firebase.auth.currentUser?.uid;
    return !!org && !!uid && org.ownerId === uid;
  }

  private buildFallbackRoleFromMember(member: OrganizationMember, organization: Organization): Role {
    const roleId = member.roleId || member.role;
    const isOwner = organization.ownerId === this.firebase.auth.currentUser?.uid;
    const isAdmin = isOwner || roleId === 'admin' || member.role === 'admin';
    return {
      id: roleId || 'member',
      name: (roleId || 'member').charAt(0).toUpperCase() + (roleId || 'member').slice(1),
      description: '',
      permissions: isAdmin ? ['*'] : DEFAULT_MEMBER_PERMISSIONS,
      isSystem: true,
      createdAt: null,
      updatedAt: null,
      createdBy: '',
    };
  }

  private buildAdminRole(): Role {
    return {
      id: 'admin',
      name: 'Admin',
      description: '',
      permissions: ['*'],
      isSystem: true,
      createdAt: null,
      updatedAt: null,
      createdBy: '',
    };
  }
}
